import { ActionHandlerRegistry } from "../dispatch/ActionHandlerRegistry";
import type { RoundActionHandler } from "../dispatch/ActionHandlerRegistry";
import { EventHandlerRegistry } from "../dispatch/EventHandlerRegistry";
import type { RoundEventHandler } from "../dispatch/EventHandlerRegistry";
import { RoundReplayEngine } from "./RoundReplayEngine";

/**
 * Shell, mechanic and game handlers, registered in list order; the engine has none of its own. Action
 * handlers fan out: several may handle one action type and run sequentially in that order.
 * Event handlers: an exact-name collision throws unless the handler sets `override: true`.
 */
export interface AdditionalReplayHandlers {
  actionHandlers?: RoundActionHandler[];
  eventHandlers?: RoundEventHandler[];
}

/**
 * The engine with the given handlers registered in list order (the plugins' handlers, flattened
 * by ClientEngine). The frame step and the canonical validation live in the engine itself.
 */
export function createDefaultRoundReplayEngine(extra?: AdditionalReplayHandlers): RoundReplayEngine {
  const eventRegistry = new EventHandlerRegistry();
  const actionRegistry = new ActionHandlerRegistry();

for (const handler of extra?.actionHandlers ?? []) {
    actionRegistry.registerHandler(handler);
  }

  for (const handler of extra?.eventHandlers ?? []) {
    if (handler.eventTypeName && eventRegistry.hasExactHandler(handler.eventTypeName) && !handler.override) {
      throw new Error(
        `Event handler collision for "${handler.eventTypeName}": a handler is already registered. ` +
          `Set override: true on the handler to replace it deliberately.`,
      );
    }
    eventRegistry.registerHandler(handler);
  }

  return new RoundReplayEngine(eventRegistry, actionRegistry);
}
